<script lang="ts">
  import { cn } from "$lib/utils/cn";
  import * as m from "$lib/paraglide/messages";
  import Label from "$lib/ui/Label.svelte";

  type Props = {
    id?: string;
    label?: string;
    value?: string[];
    /** Optional suggestions (e.g. tags already used on other clients). */
    suggestions?: string[];
    class?: string;
  };

  let {
    id = "tags",
    label,
    value = $bindable<string[]>([]),
    suggestions = [],
    class: className,
  }: Props = $props();

  let draft = $state("");

  const normalized = $derived(
    value.map((t) => t.trim()).filter(Boolean),
  );

  const suggestionPool = $derived(
    suggestions
      .map((t) => t.trim())
      .filter((t) => t && !normalized.some((x) => x.toLowerCase() === t.toLowerCase()))
      .slice(0, 12),
  );

  function addTag(raw: string) {
    const tag = raw.trim().replace(/\s+/g, " ");
    if (!tag) return;
    if (normalized.some((t) => t.toLowerCase() === tag.toLowerCase())) {
      draft = "";
      return;
    }
    value = [...normalized, tag];
    draft = "";
  }

  function removeTag(tag: string) {
    value = normalized.filter((t) => t !== tag);
  }

  function onKeydown(event: KeyboardEvent) {
    if (event.key === "Enter" || event.key === "," || event.key === "Tab") {
      if (!draft.trim()) return;
      event.preventDefault();
      addTag(draft);
    } else if (event.key === "Backspace" && !draft && normalized.length) {
      removeTag(normalized[normalized.length - 1]!);
    }
  }
</script>

<div class={cn("space-y-1.5", className)}>
  {#if label}
    <Label for={id}>{label}</Label>
  {/if}

  <div
    class="flex min-h-10 flex-wrap items-center gap-1.5 rounded-md border border-(--surface-border-strong) bg-(--surface-bg) px-2 py-1.5 focus-within:border-(--surface-accent) focus-within:ring-2 focus-within:ring-(--surface-accent)/30"
  >
    {#each normalized as tag (tag)}
      <span
        class="inline-flex items-center gap-1 rounded-md bg-(--surface-bg-sunken) px-2 py-0.5 text-xs font-medium text-(--surface-fg)"
      >
        {tag}
        <button
          type="button"
          class="text-(--surface-fg-muted) hover:text-(--surface-danger)"
          aria-label={m.form_tags_remove({ tag })}
          onclick={() => removeTag(tag)}
        >
          ×
        </button>
      </span>
    {/each}
    <input
      {id}
      class="min-w-32 flex-1 border-0 bg-transparent px-1 py-0.5 text-sm outline-none placeholder:text-(--surface-fg-subtle)"
      bind:value={draft}
      placeholder={normalized.length ? m.form_tags_add_more() : m.form_tags_placeholder()}
      onkeydown={onKeydown}
      onblur={() => {
        if (draft.trim()) addTag(draft);
      }}
    />
  </div>

  {#if suggestionPool.length}
    <div class="flex flex-wrap gap-1.5">
      {#each suggestionPool as tag (tag)}
        <button
          type="button"
          class="rounded-md border border-(--surface-border) px-2 py-0.5 text-[11px] text-(--surface-fg-muted) transition-colors hover:border-(--surface-border-strong) hover:text-(--surface-fg)"
          onclick={() => addTag(tag)}
        >
          + {tag}
        </button>
      {/each}
    </div>
  {/if}
</div>
